import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import { formatMinutesAndSeconds } from '../../utils/helpers';

const PlanDetails = ({ subscription, topUps, topUpUsage, onOpenPlanDialog, onOpenTopUpDialog, onOpenPortal }) => {
    const usagePercentage = subscription
    ? (() => {
        const monthlyLimit = subscription.monthly_limit_minutes;
        return monthlyLimit > 0 ? (subscription.used_minutes / monthlyLimit) * 100 : 0;
      })()
    : 0;

    const topUpUsagePercentage = topUpUsage && topUpUsage.total_minutes > 0
    ? (topUpUsage.used_minutes / topUpUsage.total_minutes) * 100
    : 0;

    // Calculate total top-ups purchased this month
    const getTotalTopUpsThisMonth = () => {
      if (!topUps || topUps.length === 0) return 0;
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const topUpsThisMonth = topUps.filter(topup => {
        const purchasedAt = new Date(topup.purchased_at);
        return purchasedAt >= monthStart;
      });
      return topUpsThisMonth.reduce((sum, topup) => sum + (topup.total_seconds || 0), 0) / 60;
    };

    const totalTopUpsThisMonth = getTotalTopUpsThisMonth();

    const hasTopUps = topUps && topUps.length > 0;

    // Total remaining: subscription already includes top-ups on the backend;
    // if no subscription, fall back to top-up remaining only.
    const totalRemainingMinutes = subscription
      ? subscription.remaining_minutes
      : (topUpUsage?.remaining_minutes ?? 0);

  return (
    <Box sx={{ mt: 4 }} data-test-id="plan-details">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" gutterBottom>
          Current Plan: {subscription ? subscription.plan_name.charAt(0).toUpperCase() + subscription.plan_name.slice(1) : 'No Active Plan'}
        </Typography>
        {subscription && onOpenPortal && (
          <Button
            variant="contained"
            color="primary"
            onClick={onOpenPortal}
            data-test-id="plan-manage-billing-btn"
          >
            Manage Billing
          </Button>
        )}
      </Box>

      {/* Total Remaining Time – shown whenever there is something to show */}
      {(subscription || hasTopUps) && (
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }} data-test-id="plan-total-remaining">
          <CardContent>
            <Typography variant="overline" sx={{ opacity: 0.9 }}>
              Total Remaining Time
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
              {formatMinutesAndSeconds(totalRemainingMinutes)}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Left column: subscription details OR "no subscription" card */}
        <Grid item xs={12} md={subscription || hasTopUps ? 6 : 12}>
          {subscription ? (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" color="primary">
                    Subscription Plan
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label={subscription.plan_name.charAt(0).toUpperCase() + subscription.plan_name.slice(1)} color="primary" size="small" />
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={onOpenPlanDialog}
                      data-test-id="plan-change-plan-btn"
                    >
                      Change Plan
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Monthly Limit
                  </Typography>
                  <Typography variant="h5">
                    {formatMinutesAndSeconds(subscription.monthly_limit_minutes)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Used This Month
                  </Typography>
                  <Tooltip
                    title={subscription.used_minutes < 1 && subscription.used_minutes > 0 ?
                      `${(subscription.used_minutes * 60).toFixed(1)}s` :
                      formatMinutesAndSeconds(subscription.used_minutes)}
                    arrow
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }}
                    >
                      {subscription.used_minutes < 1 && subscription.used_minutes > 0 ?
                        `${(subscription.used_minutes * 60).toFixed(1)}s` :
                        formatMinutesAndSeconds(subscription.used_minutes)}
                    </Typography>
                  </Tooltip>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Subscription Remaining
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {formatMinutesAndSeconds(subscription.subscription_remaining_minutes ?? (subscription.monthly_limit_minutes - subscription.used_minutes))}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Usage Progress
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(usagePercentage, 100)}
                        color="primary"
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(usagePercentage)}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="warning.main">
                  No Active Subscription
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You don't currently have an active subscription. Please select a plan to continue using the service.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onOpenPlanDialog}
                  size="large"
                  data-test-id="plan-select-plan-btn"
                >
                  Select a Plan
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right column: top-ups – shown when subscription exists OR when user has top-ups */}
        {(subscription || hasTopUps) && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" color="secondary">
                    Top-ups
                  </Typography>
                  {onOpenTopUpDialog && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={onOpenTopUpDialog}
                      data-test-id="plan-purchase-topup-btn"
                    >
                      Purchase Top-up
                    </Button>
                  )}
                </Box>

                {hasTopUps ? (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Total This Month
                      </Typography>
                      <Typography variant="h5" color="secondary.main">
                        {formatMinutesAndSeconds(totalTopUpsThisMonth)}
                      </Typography>
                    </Box>

                    {topUpUsage && topUpUsage.total_minutes > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography color="textSecondary" variant="body2" gutterBottom>
                          Used This Month (since month start)
                        </Typography>
                        <Tooltip
                          title={topUpUsage.used_minutes < 1 && topUpUsage.used_minutes > 0 ?
                            `${(topUpUsage.used_minutes * 60).toFixed(1)}s` :
                            formatMinutesAndSeconds(topUpUsage.used_minutes)}
                          arrow
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}
                          >
                            {topUpUsage.used_minutes < 1 && topUpUsage.used_minutes > 0 ?
                              `${(topUpUsage.used_minutes * 60).toFixed(1)}s` :
                              formatMinutesAndSeconds(topUpUsage.used_minutes)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {topUpUsage && topUpUsage.total_minutes > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography color="textSecondary" variant="body2" gutterBottom>
                          Top-ups Remaining
                        </Typography>
                        <Typography variant="h5" color="success.main">
                          {formatMinutesAndSeconds(topUpUsage.remaining_minutes)}
                        </Typography>
                      </Box>
                    )}

                    {topUpUsage && topUpUsage.total_minutes > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography color="textSecondary" variant="body2" gutterBottom>
                          Usage Progress
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(topUpUsagePercentage, 100)}
                              color="secondary"
                              sx={{ height: 8, borderRadius: 1 }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" color="text.secondary">
                              {Math.round(Math.min(topUpUsagePercentage, 100))}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography color="textSecondary" variant="body2" gutterBottom>
                      Active Top-ups ({topUps.filter(t => (t.remaining_seconds || 0) > 0).length})
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {topUps
                        .filter(topup => (topup.remaining_seconds || 0) > 0)
                        .map((topup) => (
                          <Box
                            key={topup.id}
                            sx={{
                              p: 1.5,
                              mb: 1,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2" fontWeight="medium">
                                {formatMinutesAndSeconds((topup.remaining_seconds || 0) / 60)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {topup.expires_at
                                  ? `Expires: ${new Date(topup.expires_at).toLocaleDateString()}`
                                  : 'No expiration'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      {topUps.filter(t => (t.remaining_seconds || 0) > 0).length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No active top-ups
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      No top-ups purchased yet
                    </Typography>
                    {onOpenTopUpDialog && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={onOpenTopUpDialog}
                        sx={{ mt: 1 }}
                        data-test-id="plan-purchase-first-topup-btn"
                      >
                        Purchase Your First Top-up
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PlanDetails;
